import {useTranslation} from "react-i18next";
import type {InlineRelationsData, InlineRelationsResponse} from "../types";
import {buildTopicUrl, isValidFileType} from "../contentUtils";
import {RelatedFileLink} from "../components/file/RelatedFileLink";
import {
    TOPIC_TYPE_DB_NAMES,
    TOPIC_TYPE_PATHS,
    isTopicTypePathKey,
} from "../../../paths";
import {getLangKeyFromString} from "../typeIcons";
import {ExperimentalLink} from "../../../components/ExperimentalLink";
import LinkIcon from "../../../assets/link.svg?react";
import articleClasses from "../legacy/HtmlArticleLEGACY.module.css";
import contentBaseClasses from "../contentBase.module.css";
import panelClasses from "./panelContent.module.css";

type TopicGroup = {
    name: string;
    hasSubnames: boolean;
    items: InlineRelationsData[];
};

type RelationGroup = {
    topictypeId: string;
    heading: string;
    topics: TopicGroup[];
};

// Group related entries TopicType -> Topic (source name) -> links, preserving
// first-appearance order at both levels (driven by data.related).
const groupRelations = (items: InlineRelationsData[]): RelationGroup[] => {
    const typeMap = new Map<string, RelationGroup>();

    for (const item of items) {
        let typeGroup = typeMap.get(item.topictypeId);
        if (!typeGroup) {
            const key = Number(item.topictypeId);
            typeGroup = {
                topictypeId: item.topictypeId,
                heading:
                    TOPIC_TYPE_DB_NAMES[item.topictypeId] ??
                    (isTopicTypePathKey(key)
                        ? TOPIC_TYPE_PATHS[key]
                        : undefined) ??
                    item.topictypeId,
                topics: [],
            };
            typeMap.set(item.topictypeId, typeGroup);
        }

        let topic = typeGroup.topics.find((tg) => tg.name === item.name);
        if (!topic) {
            topic = {name: item.name, hasSubnames: false, items: []};
            typeGroup.topics.push(topic);
        }
        topic.items.push(item);
        if (item.subname?.trim()) topic.hasSubnames = true;
    }

    return Array.from(typeMap.values());
};

export const PanelInlineRelations = ({
    data,
    title,
}: {
    data: InlineRelationsResponse;
    title: string;
}) => {
    const {t} = useTranslation();

    const items = data.related
        .map((id) => data.objects[id]?.data)
        .filter(Boolean);

    const groups = groupRelations(items);

    const renderLink = (item: InlineRelationsData, text: string) => {
        if (isValidFileType(item.resourceTypeId)) {
            return (
                <RelatedFileLink
                    name={item.name}
                    resourceTypeId={item.resourceTypeId}
                    resourceId={item.dataResourceId}
                />
            );
        }
        const href = buildTopicUrl(
            item.topictypeId,
            item.name,
            item.topicId,
            item.dataSegmentId
        );
        const hash = item.dataId ? `#${item.dataId}` : "";
        return (
            <ExperimentalLink href={`${href}${hash}`}>{text}</ExperimentalLink>
        );
    };

    return (
        <div
            className={`${articleClasses.content} ${contentBaseClasses.contentBase} ${panelClasses.inlineRelations}`}
        >
            {title && (
                <div className={panelClasses.relationsHeader}>
                    <LinkIcon className={panelClasses.relationsHeaderIcon} />
                    <span className={panelClasses.relationsHeaderTitle}>
                        {title}
                    </span>
                </div>
            )}

            {groups.map((group) => {
                const langKey = getLangKeyFromString(group.heading);

                return (
                    <div
                        className={panelClasses.relationGroup}
                        key={group.topictypeId}
                    >
                        <div className={panelClasses.relationGroupHeading}>
                            {langKey ? t(langKey) : group.heading}
                        </div>

                        {group.topics.map((topic) =>
                            topic.hasSubnames ? (
                                <div
                                    className={panelClasses.topicBlock}
                                    key={topic.name}
                                >
                                    <div className={panelClasses.topicHeading}>
                                        {topic.name}
                                    </div>
                                    <ul className={panelClasses.topicLinks}>
                                        {topic.items.map((item) => (
                                            <li key={item.id}>
                                                {renderLink(
                                                    item,
                                                    item.subname || item.name
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <ul
                                    className={panelClasses.topicListPlain}
                                    key={topic.name}
                                >
                                    {topic.items.map((item) => (
                                        <li key={item.id}>
                                            {renderLink(item, item.name)}
                                        </li>
                                    ))}
                                </ul>
                            )
                        )}
                    </div>
                );
            })}
        </div>
    );
};
